import { CircleCheck } from 'lucide-react';

import React, { useEffect } from 'react';

import {
  IMPORT_STEP_ORDER,
  canResumeImport,
  checklistStates,
  importProgressPercent,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Alert, Button } from '@splitfiesta/ui';

import {
  useImportJobQuery,
  useResumeImportMutation,
} from '@/hooks/queries/useImports';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { ImportCard } from './ui/ImportCard';
import { StepChecklist } from './ui/StepChecklist';

interface Props {
  jobId: string;
  onOpenGroup: (groupId: string) => void;
  /** Fires once the job reaches `done`, so the page can tick its last step. */
  onDone?: () => void;
}

export function ImportProgressStep({ jobId, onOpenGroup, onDone }: Props) {
  const { t } = useTranslation();
  useNamespace('import');
  const { isOnline } = useNetworkStatus();
  const { data: job, isError, refetch } = useImportJobQuery(jobId);
  const resume = useResumeImportMutation();

  useEffect(() => {
    // A refused resume leaves the screen on its pre-resume snapshot, and the
    // job query has already stopped polling if the job was terminal.
    if (!resume.isError) return;
    void refetch();
  }, [resume.isError, refetch]);

  const percent = importProgressPercent(job);
  const done = job?.status === 'done';

  useEffect(() => {
    if (done) onDone?.();
  }, [done, onDone]);
  const groupId = job?.groupId;
  const counters = t('import:progress.counters', {
    created: job?.counters.created ?? 0,
    total: job?.counters.total ?? 0,
  });

  const states = checklistStates(
    IMPORT_STEP_ORDER,
    job?.step,
    done ? 'done' : job?.status === 'failed' ? 'failed' : 'running'
  );
  const steps = IMPORT_STEP_ORDER.map(key => ({
    key,
    label: t(`import:progress.steps.${key}`),
    state: states[key],
  }));
  const activeStep = steps.find(step => step.state === 'active');

  // The query stops polling once it errors, so without this the screen would
  // sit at "Importing… 0 %" for good.
  if (isError && !job) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('import:progress.title')}
        </h2>
        <Alert
          type="error"
          message={t('common:errors.loadFailed')}
          data-testid="import-progress-load-error"
        />
        <Button
          data-testid="import-progress-retry"
          variant="secondary"
          onClick={() => void refetch()}
        >
          {t('common:errors.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {done && job ? (
        <div
          data-testid="import-done"
          className="flex flex-col items-center gap-3 pb-2 text-center"
        >
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-success-50 dark:bg-success-500/10">
            <CircleCheck
              className="h-10 w-10 text-success-600"
              strokeWidth={1.5}
              aria-hidden
            />
          </span>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('import:done.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {job.counters.failed > 0
              ? t('import:done.withFailures', { count: job.counters.failed })
              : t('import:done.body')}
          </p>
        </div>
      ) : (
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('import:progress.title')}
        </h2>
      )}

      <ImportCard padded data-testid="import-progress-card">
        <div
          role="progressbar"
          data-testid="import-progress-bar"
          aria-label={t('import:progress.title')}
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={counters}
          className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        >
          <div
            className="h-full rounded-full bg-primary-500 transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
        {job && (
          <p
            data-testid="import-progress-counters"
            aria-live="polite"
            className="text-base font-semibold text-gray-900 dark:text-white"
          >
            {counters}
          </p>
        )}
        <div className="h-px bg-gray-100 dark:bg-gray-700" />
        <StepChecklist testId="import-step" steps={steps} />
        {/* The step in progress, for assistive tech and the tests; the
            checklist above already shows it visually. */}
        {!done && (
          <span data-testid="import-progress-step" className="sr-only">
            {activeStep?.label ?? t('import:progress.title')}
          </span>
        )}
      </ImportCard>

      {/* Polling has stopped but the job keeps running server-side, so the
          percentage on screen is a snapshot rather than the whole story. */}
      {isError && job && (
        <div className="space-y-2">
          <Alert
            type="warning"
            message={t('common:errors.loadFailed')}
            data-testid="import-progress-poll-error"
          />
          <Button
            data-testid="import-progress-poll-retry"
            variant="secondary"
            onClick={() => void refetch()}
          >
            {t('common:errors.retry')}
          </Button>
        </div>
      )}

      {job?.failureReason && (
        <Alert type="error" message={t(job.failureReason)} />
      )}

      {resume.isError && (
        <Alert
          type="error"
          message={t('import:errors.resumeFailed')}
          data-testid="import-progress-resume-error"
        />
      )}

      {job && job.failures.length > 0 && (
        <ImportCard padded data-testid="import-progress-failures">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('import:progress.failuresTitle')}
          </h3>
          <ul className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
            {job.failures.map(failure => (
              <li key={failure.rowIndex}>
                {t('import:progress.failureRow', {
                  rowIndex: failure.rowIndex,
                  reason: t(failure.reason),
                })}
              </li>
            ))}
          </ul>
        </ImportCard>
      )}

      {job?.stalled && (
        <Alert type="warning" message={t('import:progress.stalled')} />
      )}

      {canResumeImport(job) && (
        <Button
          data-testid="import-progress-resume"
          variant="secondary"
          loading={resume.isPending}
          // Offline, the mutation would park in `paused` and the button would
          // stay in its loading state until the network came back.
          disabled={!isOnline}
          onClick={() => resume.mutate(jobId)}
        >
          {t('import:progress.resume')}
        </Button>
      )}

      {groupId && (
        <Button
          data-testid="import-progress-open-group"
          onClick={() => onOpenGroup(groupId)}
        >
          {t('import:done.openGroup')}
        </Button>
      )}
    </div>
  );
}
